import type { SummaryCardProps } from "@wayflyer/flyui";
import { Flex, InfoTooltip, Subheading, SummaryCards, Tooltip, useMediaQuery } from "@wayflyer/flyui";
import * as React from "react";
import { useIntl } from "react-intl";
import { styled } from "styled-components";

import {
  CollapsedContainer,
  CollapsedValuesContainer,
  CollapsedValuesItemContainer,
  CollapsedValuesItemTitle,
  CollapsedValuesItemValue,
  CollapseIcon,
  CollapseIconContainer,
  ExpandIcon,
  ExpandIconContainer,
  ExpandIconMobile,
  UncollapsedContainer,
} from "../../../../components/collapsing-container";
import type { InputProps } from "../../../../components/shared";
import { Inp, InputWithUnit, Spacer } from "../../../../components/shared";

export interface OptimalCacFormProps {
  title: string;
  type: string;
  summaryCards: SummaryCardProps[];
  inputs: InputProps[];
}

export interface OptimalCacCalculatorProps {
  optimalCacData: OptimalCacFormProps;
  expanded: boolean;
}

const FormContainer = styled.div`
  width: 420px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SmallSpacer = styled.span`
  margin: 5px;
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const InputContainer = styled.div``;

/**
 * OptimalCacCalculator component
 */
export const OptimalCacCalculator = ({ optimalCacData, expanded }: OptimalCacCalculatorProps): UIElement => {
  const intl = useIntl();
  const query = useMediaQuery();
  const mediumWidth = query("sm").windowGt;
  const [isExpanded, setIsExpanded] = React.useState(expanded);

  const optimalCacTooltip = intl.formatMessage({
    id: "Optimal Cac Calculator: optimal cac tooltip",
    defaultMessage:
      "Use this form to calculate the maximum CAC you can invest per customer, given the customer lifetime value and required months for breakeven.",
  });

  const optimalCacExpanded = (
    <UncollapsedContainer>
      <FormContainer>
        <form>
          <CollapseIconContainer>
            <Flex gap="1" wrap="nowrap">
              <Subheading>
                {intl.formatMessage({
                  id: "Optimal Cac Calculator: Breakeven Customer Acquisition Cost",
                  defaultMessage: "Breakeven Customer Acquisition Cost",
                })}
              </Subheading>
              <div>
                <InfoTooltip content={optimalCacTooltip} iconSize="regular" />
              </div>
            </Flex>
            <SmallSpacer />
            <Tooltip
              content={intl.formatMessage({
                id: "Optimal Cac Calculator: Click here to hide the Breakeven Customer Acquisition Cost form",
                defaultMessage: "Click here to hide the Breakeven Customer Acquisition Cost form",
              })}
            >
              <span
                role="button"
                tabIndex={-1}
                onClick={() => setIsExpanded(false)}
                onKeyDown={() => setIsExpanded(false)}
              >
                <CollapseIcon size={16} />
              </span>
            </Tooltip>
          </CollapseIconContainer>
          <SmallSpacer />
          {optimalCacData.inputs.map((props, index) => {
            return (
              <InputContainer key={`${props.name}-input-${index}`}>
                {props.unit ? <InputWithUnit {...props} /> : <Inp {...props} />}
                <Spacer />
              </InputContainer>
            );
          })}
          <SummaryCards summaryCards={optimalCacData.summaryCards} />
        </form>
      </FormContainer>
    </UncollapsedContainer>
  );

  const optimalCacCollapsed = (
    <CollapsedContainer>
      <Tooltip
        content={intl.formatMessage({
          id: "Optimal Cac Calculator: Click here to open the Breakeven Customer Acquisition Cost form",
          defaultMessage: "Click here to open the Breakeven Customer Acquisition Cost form",
        })}
      >
        <ExpandIconContainer
          role="button"
          tabIndex={-1}
          isBottom={false}
          onClick={() => setIsExpanded(true)}
          onKeyDown={() => setIsExpanded(false)}
        >
          {mediumWidth ? <ExpandIcon /> : <ExpandIconMobile />}
        </ExpandIconContainer>
      </Tooltip>
      <CollapsedValuesContainer>
        {optimalCacData.inputs.map((inputProps, index) => {
          return (
            <CollapsedValuesItemContainer key={`${inputProps.label}-data-${index}`}>
              <CollapsedValuesItemValue>{inputProps.value}</CollapsedValuesItemValue>
              <CollapsedValuesItemTitle>{inputProps.label}</CollapsedValuesItemTitle>
            </CollapsedValuesItemContainer>
          );
        })}
        {optimalCacData.summaryCards.map((summaryCardProp, index) => {
          return (
            <CollapsedValuesItemContainer key={`${summaryCardProp.title}-summary-${index}`}>
              <CollapsedValuesItemValue>{summaryCardProp.value as string}</CollapsedValuesItemValue>
              <CollapsedValuesItemTitle>{summaryCardProp.title}</CollapsedValuesItemTitle>
            </CollapsedValuesItemContainer>
          );
        })}
      </CollapsedValuesContainer>
      <Tooltip
        content={intl.formatMessage({
          id: "Optimal Cac Calculator: Click here to open the Breakeven Customer Acquisition Cost form",
          defaultMessage: "Click here to open the Breakeven Customer Acquisition Cost form",
        })}
      >
        <ExpandIconContainer
          role="button"
          tabIndex={-1}
          isBottom={true}
          onClick={() => setIsExpanded(true)}
          onKeyDown={() => setIsExpanded(false)}
        >
          {mediumWidth ? <ExpandIcon /> : <ExpandIconMobile />}
        </ExpandIconContainer>
      </Tooltip>
    </CollapsedContainer>
  );

  return isExpanded ? optimalCacExpanded : optimalCacCollapsed;
};
